/**
 * Real-time WebGPU render overlay.
 *
 * Renders on a dedicated <canvas> positioned absolutely over the R3F Canvas,
 * with pointer-events: none so camera controls in R3F still work. A CameraBridge
 * component mounted inside R3F (<Canvas>) populates a shared snapshot ref each
 * frame. The path tracer is invoked from the same `useFrame` (after the snapshot
 * copy) so it never reads a stale camera — a separate rAF can race and reset
 * accumulation every other frame, leaving the sample count stuck.
 *
 * Progressive accumulation is handled by the engine: every frame, if the camera
 * has not changed since the last frame and we're under the sample cap, we add
 * one more sample; otherwise we reset.
 *
 * While the camera is moving, internal resolution is capped (default 50% of the
 * user's resolution slider) for speed; when the view settles, full resolution
 * is applied again on the same frame (sync resize — no deferred handoff).
 */
import type { MutableRefObject } from 'react';
import { startTransition, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';

import { useFrame } from '@react-three/fiber';
import * as THREE from 'three';

import { useStore } from '../../../../pages/workspace/hooks.ts';
import { selectLampWireframeMap } from '../../../../store/selectors/workspace.selector.ts';
import { ChunkySlider } from './ChunkySlider.tsx';
import { type CameraSnapshot, PathTracerEngine, pathTracerCameraKey } from './pathtracer/engine.ts';
import type { IesGrid, LampPathtracerInput } from './pathtracer/iesTexture.ts';
import { kelvinToLinearRgb } from './pathtracer/kelvin.ts';
import { type ExtractedScene, extractScene } from './pathtracer/sceneExtract.ts';

type ResolutionPreset = 'low' | 'mid' | 'high';
const RESOLUTION_PRESETS: Record<ResolutionPreset, number> = {
  low: 0.5,
  mid: 0.75,
  high: 1,
};

interface CameraSnapshotRef {
  current: CameraSnapshot | null;
}

/**
 * Mounts inside the R3F Canvas. Populates a shared snapshot ref each frame so
 * the off-canvas path tracer can read the current camera state.
 * Reuses Vector3 / Matrix4 instances to avoid per-frame allocations (orbit = 60 alloc/s at 60fps).
 */
export function CameraBridge({
  snapshotRef,
  afterCamera,
}: {
  snapshotRef: CameraSnapshotRef;
  /** Run after writing the camera into `snapshotRef` (same useFrame). Used by the WebGPU path tracer. */
  afterCamera?: MutableRefObject<(() => void) | null>;
}) {
  const reusable = useRef<{
    position: THREE.Vector3;
    projectionMatrix: THREE.Matrix4;
    matrixWorldInverse: THREE.Matrix4;
  } | null>(null);

  useFrame(({ camera }) => {
    if (!reusable.current) {
      reusable.current = {
        position: new THREE.Vector3(),
        projectionMatrix: new THREE.Matrix4(),
        matrixWorldInverse: new THREE.Matrix4(),
      };
      snapshotRef.current = {
        position: reusable.current.position,
        projectionMatrix: reusable.current.projectionMatrix,
        matrixWorldInverse: reusable.current.matrixWorldInverse,
      };
    }
    const snap = snapshotRef.current;
    if (!snap) return;
    snap.position.copy(camera.position);
    snap.projectionMatrix.copy(camera.projectionMatrix);
    snap.matrixWorldInverse.copy(camera.matrixWorldInverse);
    afterCamera?.current?.();
  });
  return null;
}

/** Physical lamp source radius (m). Fixed; softens penumbra and the IES cone edge. */
const LAMP_SOURCE_RADIUS_M = 0.25;

/** EV passed to the path tracer present pass when the scene has no PBR texture layers. */
const EXPOSURE_SCENE_UNTEXTURED_EV = 0.9;
/** EV for textured glTFs so albedo reads closer to intended after ACES (common ~gamma display feel). */
const EXPOSURE_SCENE_TEXTURED_EV = 2.6;

function defaultExposureEvForScene(scene: ExtractedScene): number {
  const hasTextures = scene.baseColorTextures.length > 0 || scene.metallicRoughnessTextures.length > 0;
  return hasTextures ? EXPOSURE_SCENE_TEXTURED_EV : EXPOSURE_SCENE_UNTEXTURED_EV;
}
/** More samples = slightly less noise; 1st sample already shows most of the image. */
const DEFAULT_MAX_SAMPLES = 32;
const DEFAULT_IES_BLUR_DEG = 3;
const DEFAULT_COLOR_TEMP_K = 4500;
const AMBIENT_COLOR_TEMP_K = 6500;
const DEFAULT_AMBIENT = 0.5;
const DEFAULT_PATH_BOUNCES = 3;

const PT_DPR_CAP = 2;
/** While the camera is in motion, internal scale is at most this (relative to the user's resolution %). */
const MOTION_RESOLUTION_CAP = 0.5;
/** Reading sample count for the label at most this often; avoids flooding React (was ~2 setState/ frame). */
const SAMPLE_COUNT_UI_THROTTLE_MS = 120;

/** Stable key so we re-extract only when the set of model assets actually changes. */
function uploadedModelsKey(models: { name: string; url: string }[]): string {
  if (models.length === 0) return '';
  return models.map((m) => `${m.name}\0${m.url}`).join('\n');
}

interface RealTimeRenderOverlayProps {
  active: boolean;
  snapshotRef: CameraSnapshotRef;
  /** Parent sets this; we assign the per-frame path tracer step so it runs after `CameraBridge` in the same useFrame. */
  pathTracerFrameRef: MutableRefObject<(() => void) | null>;
}

type Status = 'idle' | 'initializing' | 'loadingScene' | 'rendering' | 'done' | 'error' | 'noLamps';

export function RealTimeRenderOverlay({ active, snapshotRef, pathTracerFrameRef }: RealTimeRenderOverlayProps) {
  const { t } = useTranslation();
  const { uploadedModels, activeVersion } = useStore();
  const wireframeMap = useSelector(selectLampWireframeMap);
  const uploadedModelsRef = useRef(uploadedModels);
  uploadedModelsRef.current = uploadedModels;
  /** Set after a successful `updateScene` so we do not re-extract the same data twice (bootstrap + effect). */
  const lastSceneKeyAppliedRef = useRef<string | null>(null);
  // The overlay canvas is a ref and is always rendered when active.
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);

  const engineRef = useRef<PathTracerEngine | null>(null);
  const [status, setStatus] = useState<Status>('idle');
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [sampleCount, setSampleCount] = useState(0);
  // Incremented each time a fresh engine becomes ready. Upload effects
  // (scene, lamps, tint) key off this so they re-run after the async engine
  // initialization completes - without it, the second-and-later activations
  // of this mode would never receive uploads (data already cached from the
  // first activation, so their other deps don't change) and the canvas would
  // clear to black.
  const [engineRevision, setEngineRevision] = useState(0);

  const [resolutionPreset, setResolutionPreset] = useState<ResolutionPreset>('mid');
  const resolutionScale = RESOLUTION_PRESETS[resolutionPreset];
  // Exposure follows the loaded scene (untextured vs textured); max samples / IES blur
  // stay fixed here until user/admin settings exist.
  const maxSamples = DEFAULT_MAX_SAMPLES;
  const iesBlurDeg = DEFAULT_IES_BLUR_DEG;
  // Color temperature (K) of the emitted light. 6500K is D65 (neutral white
  // on a calibrated display); 4500K is a typical neutral-white LED luminaire
  // and the most common target for architectural lighting, so it serves as a
  // better default here than pure D65.
  const [colorTempK, setColorTempK] = useState(DEFAULT_COLOR_TEMP_K);
  // Constant hemispheric fill added on ray miss. Very low default so the
  // scene still looks lamp-lit, but corners and facing-away surfaces don't
  // fall into pure black - pure path tracing with no environment looks
  // unnaturally dark, and some fill is almost always desirable.
  const [ambient, setAmbient] = useState(DEFAULT_AMBIENT);

  /** Set when "Reset" restores defaults so we do not run accumulation `invalidate` for max-samples. */
  const maxSamplesResetFromButtonRef = useRef(false);
  const exposureRef = useRef(EXPOSURE_SCENE_UNTEXTURED_EV);
  const maxSamplesRef = useRef(maxSamples);
  maxSamplesRef.current = maxSamples;
  const resolutionScaleRef = useRef(resolutionScale);
  resolutionScaleRef.current = resolutionScale;
  const isCameraMovingRef = useRef(false);
  const prevPathTracerKeyRef = useRef<string | null>(null);
  const lastEffectiveResolutionScaleRef = useRef(-1);
  /** Last `engine.currentSampleCount` (every rAF) — for done/rendering transitions, not the throttled label. */
  const lastFrameSampleCountRef = useRef(-1);
  /** Last count pushed to React state for the "n / 32" label. */
  const lastPushedSampleCountRef = useRef(-1);
  const lastSampleUiTimeRef = useRef(0);

  // Engine setup / teardown. Scene extraction runs in parallel with WebGPU init so
  // time-to-first-draw is ≈ max(T_webgpu, T_extract), not the sum of both.
  useEffect(() => {
    if (!active) return;
    let cancelled = false;
    setStatus('initializing');
    setErrorMessage(null);
    const canvas = canvasRef.current;
    if (!canvas) return;

    const modelsForBootstrap = uploadedModelsRef.current;
    const bootstrapKey = uploadedModelsKey(modelsForBootstrap);
    const sceneP = extractScene(modelsForBootstrap);

    PathTracerEngine.create(canvas)
      .then(async (engine) => {
        if (cancelled) {
          engine.dispose();
          return;
        }
        let scene;
        try {
          scene = await sceneP;
        } catch (err) {
          engine.dispose();
          if (!cancelled) {
            console.error('Scene extraction failed:', err);
            setErrorMessage(err instanceof Error ? err.message : String(err));
            setStatus('error');
          }
          return;
        }
        if (cancelled) {
          engine.dispose();
          return;
        }
        engineRef.current = engine;
        resizeEngineToContainer();
        engine.updateScene(scene);
        exposureRef.current = defaultExposureEvForScene(scene);
        lastSceneKeyAppliedRef.current = bootstrapKey;
        setStatus('loadingScene');
        setEngineRevision((r) => r + 1);
      })
      .catch((err) => {
        if (cancelled) return;
        console.error('Path tracer init failed:', err);
        setErrorMessage(err instanceof Error ? err.message : String(err));
        setStatus('error');
        void sceneP.catch(() => {
          // Avoid unhandled rejection if the GPU path fails first.
        });
      });

    return () => {
      cancelled = true;
      engineRef.current?.dispose();
      engineRef.current = null;
      lastSceneKeyAppliedRef.current = null;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [active]);

  const resizeEngineToContainer = useCallback(() => {
    const container = containerRef.current;
    const engine = engineRef.current;
    if (!container || !engine) return;
    const rect = container.getBoundingClientRect();
    const user = resolutionScaleRef.current;
    const eff = isCameraMovingRef.current ? Math.min(user, MOTION_RESOLUTION_CAP) : user;
    const dpr = Math.min(window.devicePixelRatio || 1, PT_DPR_CAP) * eff;
    const w = Math.max(1, Math.round(rect.width * dpr));
    const h = Math.max(1, Math.round(rect.height * dpr));
    engine.resize(w, h);
    lastEffectiveResolutionScaleRef.current = eff;
  }, []);

  // Resize observer on container
  useEffect(() => {
    if (!active) return;
    const container = containerRef.current;
    if (!container) return;
    const ro = new ResizeObserver(() => resizeEngineToContainer());
    ro.observe(container);
    resizeEngineToContainer();
    return () => ro.disconnect();
  }, [active, resizeEngineToContainer]);

  // Recompute buffer size when the user changes the resolution slider (still or in motion).
  useEffect(() => {
    resizeEngineToContainer();
  }, [resolutionScale, resizeEngineToContainer]);

  // Re-upload geometry when the model set changes while the overlay is already running
  // (initial upload runs in the engine effect above, in parallel with WebGPU init).
  useEffect(() => {
    if (!active) return;
    const key = uploadedModelsKey(uploadedModels);
    if (key === lastSceneKeyAppliedRef.current) return;
    const engine = engineRef.current;
    if (!engine) return;
    let cancelled = false;
    extractScene(uploadedModels)
      .then((scene) => {
        if (cancelled || !engineRef.current) return;
        engineRef.current.updateScene(scene);
        exposureRef.current = defaultExposureEvForScene(scene);
        lastSceneKeyAppliedRef.current = key;
        setStatus((prev) => (prev === 'loadingScene' ? 'rendering' : prev));
      })
      .catch((err) => {
        if (cancelled) return;
        console.error('Scene extraction failed:', err);
        setErrorMessage(err instanceof Error ? err.message : String(err));
        setStatus('error');
      });
    return () => {
      cancelled = true;
    };
  }, [active, uploadedModels]);

  // Lamps upload
  useEffect(() => {
    if (!active) return;
    const engine = engineRef.current;
    if (!engine) return;
    const lights = activeVersion?.lights ?? [];
    if (lights.length === 0) {
      setStatus('noLamps');
      engine.updateLamps([], new Map());
      return;
    }
    const lamps: LampPathtracerInput[] = [];
    const grids = new Map<string, IesGrid>();
    // One texture layer per distinct grid: luminaires sharing an IES file share the grid object.
    const keyByGrid = new Map<IesGrid, string>();
    for (const light of lights) {
      const entry = wireframeMap.get(light.uuid);
      const grid = entry?.wireframe?.iesGrid;
      if (!grid) continue;
      let iesKey = keyByGrid.get(grid);
      if (!iesKey) {
        iesKey = String(keyByGrid.size);
        keyByGrid.set(grid, iesKey);
        grids.set(iesKey, grid);
      }
      lamps.push({
        position: [light.positionX, light.positionY, light.positionZ],
        rotationZ: entry?.wireframe?.rotation ?? 0,
        iesKey,
        intensityScale: 1.0,
        radius: LAMP_SOURCE_RADIUS_M,
      });
    }
    if (lamps.length === 0) {
      const expectIesGrids = lights.some((l) => l.iesFileId);
      // Lights reference IES files but the saga has not put candela grids in the store yet
      // (common for 2–3s after load). Keep "loading" — do not show noLamps in that case.
      setStatus(expectIesGrids ? 'loadingScene' : 'noLamps');
      engine.updateLamps([], new Map());
      return;
    }
    engine.updateLamps(lamps, grids, iesBlurDeg);
    setStatus('rendering');
  }, [active, wireframeMap, activeVersion?.lights, iesBlurDeg, engineRevision]);

  // Per-frame path trace runs from `CameraBridge` useFrame (after the snapshot is written) — not a separate
  // requestAnimationFrame, which can interleave with R3F and read alternating stale/fresh cameras (stuck at 1 spp).
  useEffect(() => {
    if (!active) {
      pathTracerFrameRef.current = null;
      return;
    }
    const canvasEl = canvasRef.current;
    prevPathTracerKeyRef.current = null;
    lastFrameSampleCountRef.current = -1;
    lastPushedSampleCountRef.current = -1;

    const run = () => {
      const engine = engineRef.current;
      const snap = snapshotRef.current;
      if (!engine || !snap) return;

      const key = pathTracerCameraKey(snap);
      const prevKey = prevPathTracerKeyRef.current;
      const isMoving = prevKey !== null && key !== prevKey;
      prevPathTracerKeyRef.current = key;
      isCameraMovingRef.current = isMoving;

      const userScale = resolutionScaleRef.current;
      const effScale = isMoving ? Math.min(userScale, MOTION_RESOLUTION_CAP) : userScale;
      const lastEff = lastEffectiveResolutionScaleRef.current;
      if (lastEff < 0 || Math.abs(effScale - lastEff) > 1e-5) {
        resizeEngineToContainer();
      }

      const maxS = maxSamplesRef.current;
      const exp = exposureRef.current;
      engine.render(snap, false, { exposure: exp, maxSamples: maxS });
      const c = engine.currentSampleCount;
      const prevFrameC = lastFrameSampleCountRef.current;
      lastFrameSampleCountRef.current = c;
      if (prevFrameC !== c) {
        if (c >= maxS && prevFrameC < maxS) {
          setStatus((s) => (s === 'rendering' ? 'done' : s));
        } else if (c < maxS && prevFrameC >= maxS) {
          setStatus((s) => (s === 'done' ? 'rendering' : s));
        }
      }

      const now = performance.now();
      const lastPushed = lastPushedSampleCountRef.current;
      if (c === lastPushed) return;
      if (c < lastPushed || c >= maxS || now - lastSampleUiTimeRef.current >= SAMPLE_COUNT_UI_THROTTLE_MS) {
        lastPushedSampleCountRef.current = c;
        lastSampleUiTimeRef.current = now;
        startTransition(() => setSampleCount(c));
      }
    };

    pathTracerFrameRef.current = run;
    return () => {
      pathTracerFrameRef.current = null;
      prevPathTracerKeyRef.current = null;
      lastEffectiveResolutionScaleRef.current = -1;
      lastFrameSampleCountRef.current = -1;
      lastPushedSampleCountRef.current = -1;
      if (canvasEl) {
        canvasEl.style.transition = '';
        canvasEl.style.opacity = '';
      }
    };
  }, [active, pathTracerFrameRef, snapshotRef, resizeEngineToContainer]);

  // Invalidate when max samples changes (so we can progress beyond old cap),
  // except when the value was restored via the Reset control (we must not clear accumulation then).
  useEffect(() => {
    if (maxSamplesResetFromButtonRef.current) {
      maxSamplesResetFromButtonRef.current = false;
      return;
    }
    engineRef.current?.invalidate();
  }, [maxSamples]);

  // Push color-temperature tint to the engine. The engine itself invalidates
  // accumulation when the tint actually changes, so repeated identical
  // updates (e.g. from other re-renders) are cheap. Keyed on engineRevision
  // so the tint is reapplied after a fresh engine comes online.
  useEffect(() => {
    const engine = engineRef.current;
    if (!engine) return;
    engine.setLightTint(kelvinToLinearRgb(colorTempK));
  }, [colorTempK, engineRevision]);

  // Ambient tint is intentionally fixed and configured via one CCT constant,
  // independent from the user-facing lamp color-temperature slider.
  useEffect(() => {
    const engine = engineRef.current;
    if (!engine) return;
    engine.setAmbientTint(kelvinToLinearRgb(AMBIENT_COLOR_TEMP_K));
  }, [engineRevision]);

  useEffect(() => {
    engineRef.current?.setAmbient(ambient);
  }, [ambient, engineRevision]);

  useEffect(() => {
    const engine = engineRef.current;
    if (!engine) return;
    engine.setMaxBounces(DEFAULT_PATH_BOUNCES);
  }, [engineRevision]);

  const statusLabel = useMemo(() => {
    switch (status) {
      case 'initializing':
        return t('workspace.realtime.initializing');
      case 'loadingScene':
        return t('workspace.realtime.loadingScene');
      case 'rendering':
        return t('workspace.realtime.rendering', { done: sampleCount, total: maxSamples });
      case 'done':
        return t('workspace.realtime.done', { total: sampleCount });
      case 'noLamps':
        return t('workspace.realtime.noLamps');
      case 'error':
        return t('workspace.realtime.error', { reason: errorMessage ?? 'unknown' });
      default:
        return '';
    }
  }, [status, sampleCount, maxSamples, t, errorMessage]);

  if (!active) return null;

  return (
    <div ref={containerRef} className="absolute inset-0" style={{ pointerEvents: 'none', zIndex: 5 }}>
      <canvas
        ref={canvasRef}
        style={{
          position: 'absolute',
          inset: 0,
          width: '100%',
          height: '100%',
          display: 'block',
        }}
      />
      {/* Workaround: a 1px backdrop-filter element keeps Chrome's compositor on the un-throttled
          path. Without this, when the WebGPU canvas is the only animating surface and nothing else
          on the page invalidates, Chrome throttles BeginFrames and the UI feels sluggish. Opening
          DevTools (which forces full-rate frames) hides the symptom. The element is invisible. */}
      <div
        aria-hidden
        style={{
          position: 'absolute',
          top: 0,
          left: 0,
          width: 1,
          height: 1,
          backdropFilter: 'blur(1px)',
          WebkitBackdropFilter: 'blur(1px)',
          pointerEvents: 'none',
        }}
      />
      {/* Settings panel anchored at the bottom-center, matching the MeasurementScale placement. */}
      <div
        className="absolute bg-[#1E1E1E] border border-white/10 rounded-xl px-3 py-2 text-xs text-white shadow-2xl"
        style={{
          pointerEvents: 'auto',
          bottom: '94px',
          left: '50%',
          transform: 'translateX(-50%)',
          width: 'min(440px, 36vw)',
          zIndex: 50,
        }}
      >
        {/* Row 1: status (left) — resolution label + low/mid/high (right) */}
        <div className="flex items-center justify-between gap-3 mb-2">
          <div className="text-[11px] text-gray-400 truncate min-w-0">{statusLabel}</div>
          <div className="flex items-center gap-2 shrink-0">
            <label className="text-[11px] font-medium text-gray-300">{t('workspace.realtime.resolution')}</label>
            <div className="flex bg-[#2a2a2a] rounded-full p-0.5">
              {(['low', 'mid', 'high'] as ResolutionPreset[]).map((p) => (
                <button
                  key={p}
                  type="button"
                  onClick={() => setResolutionPreset(p)}
                  className={`px-2.5 py-0.5 text-[11px] rounded-full transition-colors capitalize ${
                    resolutionPreset === p ? 'bg-[#B3EC49] text-black font-semibold' : 'text-gray-300 hover:text-white'
                  }`}
                >
                  {t(`workspace.realtime.resolution_${p}`, p)}
                </button>
              ))}
            </div>
          </div>
        </div>

        {/* Row 2: color temp + ambient sliders side-by-side */}
        <div className="grid grid-cols-2 gap-3">
          <ChunkySlider
            label={t('workspace.realtime.colorTemp')}
            value={colorTempK}
            min={2000}
            max={10000}
            step={100}
            suffix="K"
            gradient="kelvin"
            onChange={(v) => setColorTempK(Math.round(v / 100) * 100)}
          />
          <ChunkySlider
            label={t('workspace.realtime.ambient')}
            value={ambient}
            min={0}
            max={3}
            step={0.05}
            displayValue={ambient.toFixed(2)}
            onChange={(v) => setAmbient(v)}
          />
        </div>
      </div>
    </div>
  );
}
